import React, { useEffect, useState } from 'react';
import { Target } from 'lucide-react';
import { useTranslation } from '../../hooks/useTranslation.jsx';
import useManualInput from './manualInputs/useManualInput';
import { Box, TextArea, Row, Button, Note } from './manualInputs/manualInputStyles';

/** "Otsikko | kuvaus" per line → pivot items */
function parseLines(text) {
  return text.split('\n').map((line) => {
    const [title, ...rest] = line.split('|');
    return { title: title.trim(), description: rest.join('|').trim() };
  }).filter((i) => i.title);
}

const toLines = (pivots) => pivots.map((p) => (p.description ? `${p.title} | ${p.description}` : p.title)).join('\n');

/**
 * Pivot points from a preset list (#687): the facilitator types or pastes the
 * list; saving it makes exactly these the preset pivot points.
 */
export default function StaticPivotAdmin({ instanceId, locked = false, onApplied }) {
  const { t } = useTranslation();
  const { context, busy, error, run } = useManualInput(instanceId, 'pivot_static_preset', { onApplied });
  const [text, setText] = useState('');
  const [saved, setSaved] = useState(null);

  useEffect(() => {
    if (context) setText(toLines(context.pivots.filter((p) => p.source === 'static_preset')));
  }, [context]);

  const items = parseLines(text);
  const save = async () => {
    const data = await run('apply', { items });
    if (data) setSaved(data.seed?.inserted ?? items.length);
  };

  return (
    <Box>
      <h3>{t('manualInputs.staticPivots.title')}</h3>
      <p className="intro">{t('manualInputs.staticPivots.intro')}</p>
      {context && <p className="intro">{t('manualInputs.staticPivots.current', { count: context.pivots.length })}</p>}
      <TextArea
        value={text}
        onChange={(e) => { setText(e.target.value); setSaved(null); }}
        placeholder={t('manualInputs.staticPivots.placeholder')}
        disabled={busy || locked}
      />
      <Row>
        <Button type="button" $primary onClick={save} disabled={busy || locked || !items.length}>
          <Target size={16} /> {busy ? t('manualInputs.saving') : t('manualInputs.staticPivots.save', { count: items.length })}
        </Button>
      </Row>
      {saved != null && <Note>{t('manualInputs.staticPivots.saved', { count: saved })}</Note>}
      {error && <Note $error>{error}</Note>}
      {locked && <Note $warn>{t('manualInputs.locked')}</Note>}
    </Box>
  );
}
